/**
 * @file Encargado de definir las funciones que se usarán como condiciones para alcanzar
 * los distintos pasos del flujo.
 * @module Conditions
 */
import { CQ_POTS, CQ_ACTION_CODES } from '../catalogConstants';
import { USER_TYPE } from '../bitConstants/constants';
import { stepIds } from '../flowDefinition/stepIds';
import { PRODUCT_TYPES } from '../utils/catalog';

/**
 * Método usado para depurar un paso
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true siempre
 */
export const alwaysTrue = () => {
  return true;
};


/**
 * Método que determina si está disponible la info de cliente
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si está disponible la info de cliente y false en otro caso
 */
export const isClientInfoRead = (uiNeedsState) => {
  if (uiNeedsState && uiNeedsState.clientInfo) {
    return true;
  }

  return false;
};

/**
 * Método que determina si está incompleta la info de cliente
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si está incompleta la info de cliente y false en otro caso
 */
export const clientInfoIsIncomplete = (uiNeedsState) => {
  if (uiNeedsState && uiNeedsState.clientInfo) {
    if (
      !uiNeedsState.clientInfo.contactPhone
      || !uiNeedsState.clientInfo.email
      || uiNeedsState.showPersonalData
    ) {
      if (uiNeedsState.obd
        && uiNeedsState.obd.validLines
        && Array.isArray(uiNeedsState.obd.validLines)
        && uiNeedsState.obd.validLines.length > 0
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Método que determina si se presenta la pantalla de que el producto a contratar ha cambiado
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si se determina que el producto a contratar ha cambiado
 */
export const productOfferingToBuyChanged = (uiNeedsState) => {
  if (uiNeedsState && uiNeedsState.productOfferingChanged) {
    return true;
  }
  return false;
};

/**
 * Método que determina si al cliente hay que mostrarle error de OBD
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si hay que mostrar error de OBD y false en otro caso
 */
export const isThereOBDError = (uiNeedsState) => {
  if (uiNeedsState.clientInfo
    && uiNeedsState.clientInfo.contactPhone
    && uiNeedsState.clientInfo.email
  ) {
    return false;
  }

  if (!uiNeedsState.clientInfo) {
    return false;
  }

  if (uiNeedsState.obd && !uiNeedsState.obd.validLines) {
    return false;
  }

  if (uiNeedsState
    && uiNeedsState.obd
    && uiNeedsState.obd.validLines
    && Array.isArray(uiNeedsState.obd.validLines)
    && uiNeedsState.obd.validLines.length > 0
  ) {
    return false;
  }

  return true;
};

/**
 * Método que determina si hay que mostrar el tapón por no tener residencia a un ya cliente
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si hay que mostrar el tapón false en otro caso
 */
export const isThereOBDResidenceError = (uiNeedsState) => {
  const { clientInfo, userType } = uiNeedsState;

  if (clientInfo && userType === USER_TYPE.TELCO) {
    const { permanentResidence } = clientInfo;
    if (!permanentResidence || !permanentResidence.id) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si es necesario pedir dirección de residencia a un no cliente
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir dirección de residencia false en otro caso
 */
export const permanentResidenceRequired = (uiNeedsState) => {
  const { clientInfo, userType } = uiNeedsState;

  if (clientInfo && userType === USER_TYPE.OPEN) {
    const { permanentResidence } = clientInfo;
    if (!permanentResidence || !permanentResidence.id) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si es necesario seleccionar la palanca principal (alta/migracion/portabilidad/etc)
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir palanca
 */
export const mainActionRequired = (uiNeedsState) => {
  const { mainOptionsObj } = uiNeedsState;

  if (mainOptionsObj && mainOptionsObj.mainOptions) {
    const { mainOptions } = mainOptionsObj;
    if (mainOptions) {
      const { customerQuoteItems } = mainOptions;
      if (
        customerQuoteItems
        && (customerQuoteItems.length > 1
        || (customerQuoteItems.length === 1
          && (customerQuoteItems[0].actionId === CQ_ACTION_CODES.PORTABILITY
            || (customerQuoteItems[0].actionId === CQ_ACTION_CODES.MIGRATION
              && customerQuoteItems[0].phoneOptions.length > 1))))
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Método que determina si es necesario seleccionar la palanca secundaria de movil (alta/migracion/portabilidad/etc)
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir palanca
 */
export const mobileSecondaryActionRequired = (uiNeedsState) => {
  const { mainOptionsObj, secondaryOptionsObj } = uiNeedsState;

  if (!mainOptionsObj || !mainOptionsObj.mainOptionSelected) {
    return false;
  }

  const { mainOptionSelected } = mainOptionsObj;

  if (mainOptionSelected
    && secondaryOptionsObj
    && secondaryOptionsObj.secondaryOptions
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE]
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE][mainOptionSelected]
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE][mainOptionSelected].choiceGroups
    && Array.isArray(secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE][mainOptionSelected].choiceGroups)
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE][mainOptionSelected].keepGroups
    && Array.isArray(secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE][mainOptionSelected].keepGroups)
  ) {
    const mustAskClientChoice = secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE][mainOptionSelected].choiceGroups.find(element => element.mustAskClient === true);
    const mustAskClientKeep = secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MOBILE][mainOptionSelected].keepGroups.find(element => element.mustAskClient === true);
    if (mustAskClientChoice || mustAskClientKeep) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si es necesario seleccionar la palanca secundaria de fijo (alta/migracion/portabilidad/etc)
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir palanca
 */
export const landLineSecondaryActionRequired = (uiNeedsState) => {
  const { mainOptionsObj, secondaryOptionsObj } = uiNeedsState;

  if (!mainOptionsObj || !mainOptionsObj.mainOptionSelected) {
    return false;
  }

  const { mainOptionSelected } = mainOptionsObj;

  if (mainOptionSelected
    && secondaryOptionsObj
    && secondaryOptionsObj.secondaryOptions
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.LAND_LINE]
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.LAND_LINE][mainOptionSelected]
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.LAND_LINE][mainOptionSelected].choiceGroups
    && Array.isArray(secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.LAND_LINE][mainOptionSelected].choiceGroups)
  ) {
    const mustAskClient = secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.LAND_LINE][mainOptionSelected].choiceGroups.find(element => element.mustAskClient === true);
    if (mustAskClient) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si es necesario seleccionar el número de sims
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir palanca
 */
export const numberOfMultiSimRequired = (uiNeedsState) => {
  const { mainOptionsObj, secondaryOptionsObj } = uiNeedsState;

  if (!mainOptionsObj || !mainOptionsObj.mainOptionSelected) {
    return false;
  }

  const { mainOptionSelected } = mainOptionsObj;

  if (mainOptionSelected
    && secondaryOptionsObj
    && secondaryOptionsObj.secondaryOptions
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MULTI_SIM]
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MULTI_SIM][mainOptionSelected]
    && secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MULTI_SIM][mainOptionSelected].choiceGroups
    && Array.isArray(secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MULTI_SIM][mainOptionSelected].choiceGroups)
  ) {
    const mustAskClient = secondaryOptionsObj.secondaryOptions[PRODUCT_TYPES.MULTI_SIM][mainOptionSelected].choiceGroups.length > 1;
    if (mustAskClient) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si es necesario indicar que hacer con productos
 * que se darían de baja si no se hace nada al contratar el producto actual
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario y false en caso contrario
 */
export const alreadyOwnedProductsChoicesRequired = (uiNeedsState) => {
  const { alreadyOwnedProductsChoicesObj } = uiNeedsState;

  if (alreadyOwnedProductsChoicesObj && alreadyOwnedProductsChoicesObj.mustAskClient) {
    return true;
  }

  return false;
};


/**
 * Método que determina si es necesario seleccionar la palanca principal (alta/migracion/portabilidad/etc)
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir palanca
 */
export const portabilityOTPRequired = (uiNeedsState) => {
  const { portabilityOTPNeeded } = uiNeedsState;

  if (portabilityOTPNeeded) {
    return true;
  }

  return false;
};

/**
 * Método que determina si se deben elegir plazos de permanencia (commitments)
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si se deben elegir plazos de permanencia (commitments)
 */
export const commitmentSelectionRequired = (uiNeedsState) => {
  const { addToCartNeeds } = uiNeedsState;

  if (addToCartNeeds && Array.isArray(addToCartNeeds.commitmentOptions) && addToCartNeeds.commitmentOptions.length > 1) {
    return true;
  }

  return false;
};

/**
 * Método que determina si es está disponible la instalación inmediata (ppip)
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es está disponible el ppip
 */
export const isPpipAvailableCondition = (uiNeedsState) => {
  const { ppipAvailable } = uiNeedsState;

  if (ppipAvailable) {
    return true;
  }

  return false;
};

/**
 * Método que determina si es necesario seleccionar un producto padre para comenzar la contratación
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir que seleccione producto padre
 */
export const parentProductsToSelectRequired = (uiNeedsState) => {
  const { parentProductsToSelect } = uiNeedsState;

  if (parentProductsToSelect) {
    if (parentProductsToSelect.length > 1) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si es necesario seleccionar un producto padre para postventa para comenzar la contratación
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si es necesario pedir que seleccione producto padre
 */
export const afterSaleParentProductsToSelectRequired = (uiNeedsState) => {
  const { afterSaleParentProductsToSelect } = uiNeedsState;

  if (afterSaleParentProductsToSelect) {
    if (afterSaleParentProductsToSelect.length > 1) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si el pedido requiere envío logístico
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si el pedido requiere envío logístico false en otro caso
 */
export const isHomeShippingRequired = (uiNeedsState) => {
  const { updateCQNeeds, poitReceived } = uiNeedsState;

  const { shipping, installation, sendInstallationAddressIfInstallerSelected, shippingStepAllowed } = updateCQNeeds;

  let autoresolvePoitSelection;
  if(installation && installation.length > 1){
    const poitFound = installation.find((term) => {
      return term.id === poitReceived;
    });
    if(poitFound){
      autoresolvePoitSelection = true;
    }
  }

  if ((shipping && shipping.length === 1)
    && (!autoresolvePoitSelection
  || (autoresolvePoitSelection && poitReceived !== CQ_POTS.INSTALLATION_POT.INSTALLATION_ID) 
  || (autoresolvePoitSelection && poitReceived === CQ_POTS.INSTALLATION_POT.INSTALLATION_ID && !sendInstallationAddressIfInstallerSelected))) {
    const shippingType = shipping[0].id;

    if (shippingType === CQ_POTS.SHIPPING_POT.HOME_SHIPPING_ID && shippingStepAllowed) {
      return true;
    }
  }

  return false;
};

/**
 * Método que determina si el pedido requiere que se elija si se quiere instalación del dispositivo
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si el pedido requiere eleccion de instalación del dispositivo
 */
export const isDeviceInstallationChoiceRequired = (uiNeedsState) => {
  const { updateCQNeeds, poitReceived } = uiNeedsState;

  const { installation, poitSelected } = updateCQNeeds;

  let autoresolvePoitSelection;
  if(installation && installation.length > 1){
    const poitFound = installation.find((term) => {
      return term.id === poitReceived;
    });
    if(poitFound){
      autoresolvePoitSelection = true;
    }
  }

  if (installation && installation.length > 1 && !autoresolvePoitSelection && !poitSelected) {
    return true;
  }
  return false;
};

/**
 * Método que determina si el pedido requiere cuenta bancaria
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si el pedido requiere cuenta bancaria false en otro caso
 */
export const isCustomerAccountRequired = (uiNeedsState) => {
  const { updateCANeeds } = uiNeedsState;

  const { requiredAccount, accountInfo } = updateCANeeds;

  if (requiredAccount && !accountInfo) {
    return true;
  }
  return false;
};

/**
 * Método que determina si se debe ir a página de riesgo
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si el pedido queda bloqueado por riesgo
 */
export const isRiskStepRequired = (uiNeedsState) => {
  const { accountWithRisk, clientWithRisk } = uiNeedsState;

  if (accountWithRisk || clientWithRisk) {
    return true;
  }
  return false;
};

/**
 * Método que determina si el cliente tiene direcciones para elegir
 * la dirección de instalación
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si el cliente quiere una nueva dirección de instalación
 */
export const shouldShowInstallationStep = (uiNeedsState) => {
  const {
    showInstallationStep,
  } = uiNeedsState;

  if (!showInstallationStep) {
    return false;
  }

  return true;
};


/**
 * Método que determina si se tienes los precios disponibles para pintar el carrito
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si los precios están disponibles, false en otro caso
 */
export const arePricesAvailable = (uiNeedsState) => {
  const { pricesInfo } = uiNeedsState;

  if (
    pricesInfo
    && (pricesInfo.oneTimeCharges || pricesInfo.recurringCharges)
  ) {
    return true;
  }

  return false;
};

/**
 * Método que determina si se tienen los términos legales disponibles
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si términos legales están disponibles, false en otro caso
 */
export const areLegalTermsAvailable = (uiNeedsState) => {
  const { legalTerms } = uiNeedsState;

  if (legalTerms && legalTerms.termsObtained) {
    return true;
  }
  return false;
};

/**
 * Método que determina si existe CO creada o si el pedido ha sido enviado a SE
 * @param {obj} uiNeedsState Redux uiNeeds state
 * @returns true si hay CO creada, false en otro caso
 */
export const isConfirmationAvailable = (uiNeedsState) => {
  const { confirmationInfo } = uiNeedsState;

  if (confirmationInfo && (confirmationInfo.idCO || confirmationInfo.idSE || confirmationInfo.genesysOK)) {
    return true;
  }

  return false;
};

/**
 * Método para mostrar
 * @returns true siempre
 */
export const scoringInfoRequired = (uiNeedsState) => {
  const { riskEvaluationRequirements } = uiNeedsState;

  if (riskEvaluationRequirements) {
    return true;
  }

  return false;
};


export const isCardPayment = (uiNeedsState) => {
  const { cardPaymentInfo } = uiNeedsState;
  if (cardPaymentInfo && cardPaymentInfo.paymentUrl) {
    return true;
  }
  return false;
};

export const isMyProductsLoaded = (uiNeedsState) => {
  const { myProductsInfo } = uiNeedsState;

  if (myProductsInfo && myProductsInfo.loaded) {
    return true;
  }

  return false;
};

/**
 * Método para mostrar un paso obligatorio
 * @returns true siempre
 */
export const alwaysShowStep = () => {
  return true;
};

/**
 * Método para no mostrar un paso por flujo de navegación normal
 * @returns true siempre
 */
export const neverShowStep = () => {
  return false;
};

/**
 * Método para mostrar un paso cuando se hayan recuperado más de una línea posible para realizar el otp
 * @returns true si existe más de una línea para el otp, false en caso contrario
 */
export const areThereMultipleLinesForOTP = (uiNeedsState) => {
  const { legalTerms } = uiNeedsState;
  if (legalTerms && legalTerms.phoneNumbersOTP && legalTerms.phoneNumbersOTP.length > 1) {
    return true;
  }
  return false;
};

/**
 * Método para mostrar un paso cuando se hayan confirmado la pantalla de resumen de dispositivo
 * @returns true si se ha confirmado la pantalla de resumen de dispositivo, false en caso contrario
 */
export const isDeviceOrPackSummaryConfirmed = (uiNeedsState) => {
  const { confirmedSteps } = uiNeedsState;
  if (confirmedSteps) {
    const summaryConfirmed = confirmedSteps.find(step => (step === stepIds.deviceSummary || step === stepIds.packSummary));
    if (summaryConfirmed) {
      return true;
    }
  }
  return false;
};

/**
 * Método para mostrar un paso cuando se haya recuperado la url del iframe y el numero para realizar el otp
 * @returns true si se ha obtenido la url del iframe y el numero para realizar el otp
 */
export const isSignatureUrlOTP = (uiNeedsState) => {
  const { signatureOTP } = uiNeedsState;
  if (signatureOTP && signatureOTP.phoneNumberOTP && signatureOTP.signatureURL) {
    return true;
  }
  return false;
};

/**
 * Método para mostrar un paso cuando el cliente tenga deuda y haya confirmado el resumen
 * @returns true si se tiene que mostrar el paso y false en otro caso
 */
export const isClientWithDebt = (uiNeedsState) => {
  const { clientWithDebt, confirmedSteps } = uiNeedsState;
  if (clientWithDebt && confirmedSteps.includes(stepIds.manualSummary)) {
    return true;
  }
  return false;
};

/**
 * Método para mostrar un paso cuando el cliente no tenga crédito y haya confirmado el resumen
 * @returns true si se tiene que mostrar el paso y false en otro caso
 */
export const isClientWithoutAvailableCredit = (uiNeedsState) => {
  const { clientWithoutAvailableCredit, confirmedSteps } = uiNeedsState;
  if (clientWithoutAvailableCredit && confirmedSteps.includes(stepIds.manualSummary)) {
    return true;
  }
  return false;
};

/**
 * Método para mostrar un paso cuando el cliente tenga riesgo y haya confirmado el resumen
 * @returns true si se tiene que mostrar el paso y false en otro caso
 */
export const isClientWithUnknownRisk = (uiNeedsState) => {
  const { clientWithUnknownRisk, confirmedSteps } = uiNeedsState;
  if (clientWithUnknownRisk && confirmedSteps.includes(stepIds.manualSummary)) {
    return true;
  }
  return false;
};
